'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ArrowUpRight,
  Clock,
  Loader2,
  Mic,
  Search,
  SlidersHorizontal,
  Star,
  Subtitles,
  TrendingUp,
  X,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router';

import { BestMatchCard } from '@/components/anime/best-match-card';
import { SearchTrigger } from '@/components/search/search-trigger';
import { VoiceSearchButton } from '@/components/search/voice-search-button';
import Image from '@/components/ui/image';
import { useRecentSearches } from '@/hooks/use-recent-searches';
import { useTrendingAnime } from '@/hooks/use-trending-anime';
import type { Anime, AnimePage } from '@/lib/yozora/types';
import { cn } from '@/lib/utils';

// ─── Hotkey ─────────────────────────────────────────────────────────────────

function useCmdKHotkey(
  open: boolean,
  onOpenChange: (next: boolean) => void
): void {
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const isCmdK =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isCmdK) return;
      event.preventDefault();
      onOpenChange(!open);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);
}

// ─── Search hook ────────────────────────────────────────────────────────────

interface SearchState {
  results: Anime[];
  loading: boolean;
  error: string | null;
}

function useAnimeSearch(query: string): SearchState {
  const [state, setState] = React.useState<SearchState>({
    results: [],
    loading: false,
    error: null,
  });

  React.useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setState({ results: [], loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({
        search: trimmed,
        page: '1',
        perPage: '8',
        sort: 'POPULARITY_DESC',
      });
      fetch(`/api/animes?${params.toString()}`, { signal: controller.signal })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const body = (await res.json()) as AnimePage | { error: string };
          if (cancelled) return;
          if (!('data' in body)) {
            setState({ results: [], loading: false, error: 'Search failed' });
            return;
          }
          setState({ results: body.data, loading: false, error: null });
        })
        .catch((err) => {
          if (cancelled || (err as Error).name === 'AbortError') return;
          setState({
            results: [],
            loading: false,
            error: (err as Error).message || 'Search failed',
          });
        });
    }, 220);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query]);

  return state;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getTitle(anime: Anime): string {
  return (
    anime.title?.english ||
    anime.title?.romaji ||
    anime.title?.native ||
    'Untitled'
  );
}

function getCover(anime: Anime): string {
  return (
    anime.poster?.large ||
    anime.poster?.medium ||
    anime.poster?.small ||
    '/png/thumbnail.png'
  );
}

function shortRating(anime: Anime): string | null {
  const raw = anime.age_rating?.rating ?? '';
  const trimmed = raw.split('-')[0]?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

function scoreText(anime: Anime): string | null {
  if (!anime.score || anime.score <= 0) return null;
  return (anime.score / 10).toFixed(1);
}

function formatLabel(format: string | null | undefined): string | null {
  if (!format) return null;
  if (format === 'TV') return 'TV';
  if (format === 'MOVIE') return 'Movie';
  if (format === 'TV_SHORT') return 'TV Short';
  return format.replaceAll('_', ' ');
}

// ─── Result rows ────────────────────────────────────────────────────────────

function ResultRow({
  anime,
  active,
  onSelect,
  onHover,
}: {
  anime: Anime;
  active: boolean;
  onSelect: (id: number) => void;
  onHover: () => void;
}): React.ReactElement {
  const title = getTitle(anime);
  const score = scoreText(anime);
  const rating = shortRating(anime);
  const format = formatLabel(anime.format);
  const year = anime.season_year;
  const subCount = anime.sub_count ?? 0;
  const dubCount = anime.dub_count ?? 0;

  return (
    <button
      type="button"
      data-active={active}
      onClick={() => onSelect(anime.id)}
      onMouseMove={onHover}
      className={cn(
        'group flex w-full items-center gap-3 rounded-xl border px-2 py-2 text-left',
        'transition-colors',
        active
          ? 'border-border/60 bg-card/60'
          : 'border-transparent hover:border-border/60 hover:bg-card/60',
        'focus:outline-none'
      )}
    >
      <div className="relative h-14 w-10 flex-shrink-0 overflow-hidden rounded-lg shadow-sm ring-1 ring-border/60">
        <Image
          src={getCover(anime)}
          alt={title}
          fill
          sizes="40px"
          className="object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'line-clamp-1 text-sm font-medium transition-colors',
            active
              ? 'text-foreground'
              : 'text-foreground/90 group-hover:text-foreground'
          )}
        >
          {title}
        </span>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
          {score && (
            <span className="flex items-center gap-0.5">
              <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
              <span className="tabular-nums">{score}</span>
            </span>
          )}
          {rating && (
            <span className="rounded border border-border/70 bg-muted/60 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-foreground/80">
              {rating}
            </span>
          )}
          {format && (
            <>
              {(score || rating) && (
                <span className="h-1 w-1 rounded-full bg-border" />
              )}
              <span>{format}</span>
            </>
          )}
          {year && (
            <>
              <span className="h-1 w-1 rounded-full bg-border" />
              <span className="tabular-nums">{year}</span>
            </>
          )}
          {subCount > 0 && (
            <>
              <span className="h-1 w-1 rounded-full bg-border" />
              <span className="inline-flex items-center gap-0.5 tabular-nums">
                <Subtitles className="h-3 w-3" />
                {subCount}
              </span>
            </>
          )}
          {dubCount > 0 && (
            <>
              <span className="h-1 w-1 rounded-full bg-border" />
              <span className="inline-flex items-center gap-0.5 tabular-nums">
                <Mic className="h-3 w-3" />
                {dubCount}
              </span>
            </>
          )}
        </div>
      </div>

      <ArrowUpRight
        className={cn(
          'h-3.5 w-3.5 flex-shrink-0 transition-colors',
          active ? 'text-primary' : 'text-muted-foreground/40 group-hover:text-primary'
        )}
      />
    </button>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

interface CommandSearchProps {
  isExpanded?: boolean;
  className?: string;
  showTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
}

function CommandSearchInner({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}): React.ReactElement {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const { results, loading, error } = useAnimeSearch(query);
  const { recent, add: addRecent, remove: removeRecent, clear: clearRecent } =
    useRecentSearches();
  const trimmed = query.trim();
  const hasQuery = trimmed.length > 0;
  // "Trending on 1Anime" suggestions for the empty state.
  const { items: trending, loading: trendingLoading } = useTrendingAnime(
    open && !hasQuery
  );
  // The list arrow keys / Enter operate on: search results when querying,
  // otherwise the trending suggestions.
  const navList = hasQuery ? results : trending;

  // Reset when the dialog closes; reset on every new list so the highlight
  // always lands on the first (best) item.
  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);
  React.useEffect(() => {
    setActiveIndex(0);
  }, [results, trending, hasQuery]);

  // Autofocus input on open. Radix focuses its first focusable child
  // by default; the keyboard nav lives on the input below anyway, so
  // we just need the cursor in the field.
  React.useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const handleSelect = React.useCallback(
    (id: number): void => {
      if (trimmed) addRecent(trimmed);
      onOpenChange(false);
      navigate(`/anime/${id}`);
    },
    [navigate, onOpenChange, trimmed, addRecent]
  );

  // Keep the active row scrolled into view when arrow-key navigation
  // moves past the viewport. `nearest` avoids jumpy scroll on each
  // press; only triggers when the row is off-screen.
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const target = list.querySelector<HTMLElement>(
      `[data-result-index="${activeIndex}"]`
    );
    target?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (navList.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % navList.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + navList.length) % navList.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = navList[activeIndex];
      if (target) handleSelect(target.id);
    }
  };

  const applyRecent = (value: string): void => {
    setQuery(value);
    inputRef.current?.focus();
  };

  const [best, ...rest] = results;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-background/60 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0'
          )}
        />
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'fixed left-[50%] top-[10%] z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-[50%]',
            'gap-0 sm:max-w-xl',
            'overflow-hidden rounded-2xl border border-border/60 bg-card/95 shadow-2xl backdrop-blur-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'data-[state=open]:slide-in-from-top-4 data-[state=closed]:slide-out-to-top-4'
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Search anime
          </DialogPrimitive.Title>

          {/* Ambient backdrop wash */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/8 via-card/0 to-transparent"
          />

          {/* Input row */}
          <div className="relative flex items-center gap-2.5 border-b border-border/60 px-4 py-3.5">
            <Search className="h-4 w-4 flex-shrink-0 text-primary/70" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search anime…"
              className={cn(
                'min-w-0 flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground/70',
                'focus:outline-none'
              )}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {loading && (
              <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary/70" />
            )}
            <VoiceSearchButton
              onResult={(transcript) => {
                setQuery(transcript);
                inputRef.current?.focus();
              }}
            />
            <kbd className="hidden h-5 select-none items-center rounded border border-border/60 bg-background/70 px-1.5 text-[10px] font-medium text-muted-foreground sm:inline-flex">
              Esc
            </kbd>
          </div>

          {/* Body */}
          <div
            ref={listRef}
            className="relative max-h-[60vh] overflow-y-auto p-3 sm:p-4"
          >
            {!hasQuery && (
              <div className="space-y-4">
                {recent.length > 0 && (
                  <div>
                    <div className="mb-1.5 flex items-center justify-between px-1">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        Recent searches
                      </p>
                      <button
                        type="button"
                        onClick={clearRecent}
                        className="text-[10px] font-medium text-muted-foreground/70 transition-colors hover:text-foreground"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {recent.map((value) => (
                        <span
                          key={value}
                          className="inline-flex items-center rounded-full border border-border/60 bg-card/60 text-xs text-foreground/90 transition-colors hover:border-primary/50 hover:bg-primary/10"
                        >
                          <button
                            type="button"
                            onClick={() => applyRecent(value)}
                            className="max-w-[160px] truncate py-1 pl-3 pr-1.5 focus:outline-none"
                          >
                            {value}
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove ${value}`}
                            onClick={() => removeRecent(value)}
                            className="mr-1 flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(trendingLoading || trending.length > 0) && (
                  <div>
                    <p className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold tracking-wide text-muted-foreground">
                      <TrendingUp className="h-3 w-3 text-primary" />
                      Trending on 1Anime
                    </p>
                    {trending.length === 0 ? (
                      <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading…
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        {trending.map((anime, i) => (
                          <div key={anime.id} data-result-index={i}>
                            <ResultRow
                              anime={anime}
                              active={activeIndex === i}
                              onSelect={handleSelect}
                              onHover={() => setActiveIndex(i)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {recent.length === 0 &&
                  !trendingLoading &&
                  trending.length === 0 && (
                    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                      <div className="relative">
                        <div className="absolute inset-0 -z-10 rounded-full bg-primary/10 blur-xl" />
                        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                          <Search className="h-5 w-5 text-primary" />
                        </div>
                      </div>
                      <p className="text-sm font-medium text-foreground/80">
                        Search across 1Anime
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Start typing a title to find anime.
                      </p>
                    </div>
                  )}
              </div>
            )}

            {hasQuery && error && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-center text-xs text-destructive">
                {error}
              </div>
            )}

            {hasQuery && !error && !loading && results.length === 0 && (
              <div className="py-12 text-center text-xs text-muted-foreground">
                No results for{' '}
                <span className="font-semibold text-foreground/80">
                  &ldquo;{trimmed}&rdquo;
                </span>
              </div>
            )}

            {hasQuery && best && (
              <div className="space-y-3">
                <div data-result-index={0}>
                  <BestMatchCard
                    anime={best}
                    size="sm"
                    active={activeIndex === 0}
                    onSelect={handleSelect}
                    onHover={() => setActiveIndex(0)}
                  />
                </div>
                {rest.length > 0 && (
                  <div>
                    <p className="px-2 pb-2 pt-1 text-[11px] font-semibold tracking-wide text-muted-foreground">
                      More results
                    </p>
                    <div className="space-y-0.5">
                      {rest.map((anime, i) => {
                        const index = i + 1;
                        return (
                          <div key={anime.id} data-result-index={index}>
                            <ResultRow
                              anime={anime}
                              active={activeIndex === index}
                              onSelect={handleSelect}
                              onHover={() => setActiveIndex(index)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer bar — Advanced Search entry point on the left,
            * keyboard hints on the right (right side only renders
            * when there are results to navigate). */}
          <div className="relative flex items-center justify-between gap-3 border-t border-border/60 bg-background/40 px-3 py-2 text-[10px] text-muted-foreground">
            <button
              type="button"
              onClick={() => {
                if (trimmed) addRecent(trimmed);
                onOpenChange(false);
                // Pre-fill the advanced search page with whatever the
                // user has already typed so the transition is
                // continuous rather than starting from a blank slate.
                const target = trimmed
                  ? `/search?q=${encodeURIComponent(trimmed)}`
                  : '/search';
                navigate(target);
              }}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1',
                'text-[10px] font-semibold tracking-wide text-primary',
                'transition-colors hover:border-primary/60 hover:bg-primary/20',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60'
              )}
            >
              <SlidersHorizontal className="h-3 w-3" />
              Advanced Search + Filters
            </button>

            {navList.length > 0 ? (
              <div className="flex items-center gap-3">
                <span className="hidden items-center gap-1 sm:flex">
                  <kbd className="inline-flex h-4 select-none items-center rounded border border-border/60 bg-background/70 px-1 font-medium">
                    ↑
                  </kbd>
                  <kbd className="inline-flex h-4 select-none items-center rounded border border-border/60 bg-background/70 px-1 font-medium">
                    ↓
                  </kbd>
                  navigate
                </span>
                <span className="hidden items-center gap-1 sm:flex">
                  <kbd className="inline-flex h-4 select-none items-center rounded border border-border/60 bg-background/70 px-1 font-medium">
                    ↵
                  </kbd>
                  open
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="inline-flex h-4 select-none items-center rounded border border-border/60 bg-background/70 px-1 font-medium">
                    esc
                  </kbd>
                  close
                </span>
              </div>
            ) : (
              <span className="flex items-center gap-1">
                <kbd className="inline-flex h-4 select-none items-center rounded border border-border/60 bg-background/70 px-1 font-medium">
                  esc
                </kbd>
                close
              </span>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function CommandSearch({
  isExpanded = false,
  className = '',
  showTrigger = true,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CommandSearchProps): React.ReactElement {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const onOpenChange = controlledOnOpenChange ?? setUncontrolledOpen;

  useCmdKHotkey(open, onOpenChange);

  return (
    <>
      {showTrigger && (
        <SearchTrigger
          isExpanded={isExpanded}
          className={className}
          onClick={() => onOpenChange(true)}
        />
      )}
      <CommandSearchInner open={open} onOpenChange={onOpenChange} />
    </>
  );
}

CommandSearch.defaultProps = {
  className: '',
  isExpanded: false,
  showTrigger: true,
  open: undefined,
  onOpenChange: undefined,
};
